import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HelperService } from '../helper/helper.service';
import { TxService } from '../shared/tx.service';
import { SmartHttpService } from '../smart-http/smart-http.service';
import {
  normalizeAirportList,
  readStoredLatLong,
} from '../airport/airport-list.util';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { localStorageKeys } from '../localStorage/localStorageVariables';

export interface AirportServicesPassenger {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

export interface AirportServicesContact {
  email: string;
  phone: string;
  countryCode?: string;
  [key: string]: any;
}

export interface AirportServicesBookingPayload {
  bookingReferenceId: string;
  bookingReference: string;
  passengers?: AirportServicesPassenger[];
  contact?: AirportServicesContact;
  command?: 'CREATE' | 'FETCH';
}

export interface AppliedFilters {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AirportServicesBookingService {
  private apiParams: any;

  constructor(
    private httpClient: SmartHttpService,
    private txService: TxService,
    private helperService: HelperService,
    private localStorageService: LocalStorageService
  ) {
    this.initApiParams();
  }

  // ============ API URL Getters ============

  private get searchApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesSearchApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get searchEsimApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesEsimServicesApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get getAirportListApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesGetAirportListApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get carouselsApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesCarouselsApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get carouselTabsApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesCarouselTabsApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get carouselItemsApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesCarouselItemsApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get createBookingApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesCreateBookingApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get fetchBookingApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesFetchBookingApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get bookingApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesBookingApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get fetchBookingStatusApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesFetchBookingStatusApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get getBookingDetailsApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesGetBookingDetailsApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get getBookingHistoryApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesGetBookingHistoryApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get getBookingPoliciesApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesBookingPoliciesApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get addUserActionApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.addUserActionApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  private get productAvailabilityApi(): string {
    const finalRequestParams = {
      ...this.txService.getDefaultApiParams(),
      ...this.apiParams,
    };
    return `${this.txService.getAirportServicesProductAvailabilityApi()}?${this.helperService.serializeToGetParams(
      finalRequestParams
    )}`;
  }

  // ============ API Request Methods ============

  searchProducts(
    q: string,
    page: number = 0,
    size: number = 20,
    fq?: string
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.q = q;
    this.apiParams.page = page;
    this.apiParams.size = size;
    if (fq) {
      this.apiParams.fq = fq;
    }
    return this.httpClient.getWithErorrHandler(this.searchApi);
  }

  searchEsim(
    q: string,
    page: number = 0,
    size: number = 20,
    fq?: string
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.q = q;
    this.apiParams.page = page;
    this.apiParams.size = size;
    if (fq) {
      this.apiParams.fq = fq;
    }
    return this.httpClient.getWithErorrHandler(this.searchEsimApi);
  }

  /**
   * api/airportServices/getAirportList
   * `airport` is required by the endpoint; page is zero-based and the size
   * param is named `perpage`. `pid` falls back to the partner id server-side,
   * so it only needs sending when overriding the current partner.
   * `type` scopes the list to a service (e.g. `lounge`, `fasttrack`) and is
   * omitted when not given.
   */
  getAirportList(
    airport: string,
    page: number = 0,
    perpage: number = 20,
    tierId?: string,
    subscriptionId?: string,
    pid?: string,
    type?: string
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.airport = airport;
    this.apiParams.page = page;
    this.apiParams.perpage = perpage;
    if (tierId) {
      this.apiParams.tier_id = tierId;
    }
    if (subscriptionId) {
      this.apiParams.tier_subscription_id = subscriptionId;
    }
    if (pid) {
      this.apiParams.pid = pid;
    }
    if (type) {
      this.apiParams.type = type;
    }
    // The endpoint sorts by proximity when given the user's coordinates; they
    // are sent whenever location has been captured and left off otherwise.
    const coords = readStoredLatLong(
      this.localStorageService.getItem(localStorageKeys.LAT_LONG_DATA)
    );
    if (coords) {
      this.apiParams.latitude = coords.latitude;
      this.apiParams.longitude = coords.longitude;
    }
    return this.httpClient.getWithErorrHandler(this.getAirportListApi).pipe(
      // The endpoint answers { status, result: [...], totalResults }, with the
      // airport name under `title`. Normalize each entry so callers reading
      // `airportName` keep working while `title` and the terminal/entity
      // extras stay on the object.
      map((res: any) => ({
        ...res,
        result: normalizeAirportList(res?.result ?? res?.results),
      }))
    );
  }

  /**
   * Airport search for a single airport service (`lounge`, `fasttrack`) via
   * api/airportServices/getAirportList. That endpoint returns the list under
   * `result`; it is exposed as `results` so callers keep the same shape they
   * used with the old airport-services search.
   */
  getServiceAirportList(
    airport: string,
    page: number = 0,
    perpage: number = 20,
    type: string
  ): Observable<any> {
    return this.getAirportList(
      (airport || '').trim(),
      page,
      perpage,
      undefined,
      undefined,
      undefined,
      type
    ).pipe(
      map((res: any) => ({
        ...res,
        results: res?.result ?? res?.results ?? [],
      }))
    );
  }

  getCarousels(
    type: string,
    slug: string,
    tierId?: string,
    subscriptionId?: string,
    appliedFilters?: AppliedFilters,
    sort?: string,
    page: number = 0,
    size: number = 5
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.type = type;
    this.apiParams.slug = slug;
    if (sort) {
      this.apiParams.sort = sort;
    }
    if (page) {
      this.apiParams.page = page;
    }
    if (size) {
      this.apiParams.size = size;
    }
    // tierId and subscriptionId are not sent in query params

    if (appliedFilters) {
      return this.httpClient.postWithErorrHandler(this.carouselsApi, {
        appliedFilters,
      });
    }
    return this.httpClient.getWithErorrHandler(this.carouselsApi);
  }

  getCarouselTabs(
    slug: string,
    tierId?: string,
    subscriptionId?: string
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.slug = slug;
    if (tierId) {
      this.apiParams.tier_id = tierId;
    }
    if (subscriptionId) {
      this.apiParams.tier_subscription_id = subscriptionId;
    }
    return this.httpClient.getWithErorrHandler(this.carouselTabsApi);
  }

  getCarouselItems(
    slug: string,
    tierId?: string,
    subscriptionId?: string,
    appliedFilters?: AppliedFilters
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.slug = slug;
    if (tierId) {
      this.apiParams.tier_id = tierId;
    }
    if (subscriptionId) {
      this.apiParams.tier_subscription_id = subscriptionId;
    }

    const payload = appliedFilters ? { appliedFilters } : {};
    return this.httpClient.postWithErorrHandler(this.carouselItemsApi, payload);
  }

  createBooking(payload: any): Observable<any> {
    this.initApiParams();
    return this.httpClient.postWithErorrHandler(this.createBookingApi, payload);
  }

  loungecarosol(
    type: string,
    slug: string,
    tierId?: number | string,
    subscriptionId?: string,
    appliedFilters?: AppliedFilters
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.type = type;
    this.apiParams.slug = slug;

    if (appliedFilters) {
      const body: any = {
        pid: this.txService.getAppPid,
        appliedFilters,
      };
      if (tierId != null && tierId !== '') {
        body.tierId =
          typeof tierId === 'string' ? parseInt(tierId, 10) : tierId;
      }
      if (subscriptionId) {
        body.subscriptionId = subscriptionId;
      }
      return this.httpClient.postWithErorrHandler(this.carouselsApi, body);
    }
    return this.httpClient.getWithErorrHandler(this.carouselsApi);
  }

  fetchBooking(
    bookingReferenceId: string,
    bookingReference: string,
    passengers: AirportServicesPassenger[] = [],
    contact?: AirportServicesContact
  ): Observable<any> {
    this.initApiParams();
    const payload: AirportServicesBookingPayload = {
      bookingReferenceId,
      bookingReference,
      passengers,
      contact,
    };
    return this.httpClient.postWithErorrHandler(this.fetchBookingApi, payload);
  }

  productBooking(paymentIntentId: string): Observable<any> {
    this.initApiParams();
    const payload = {
      paymentIntentId: paymentIntentId,
    };
    return this.httpClient.postWithErorrHandler(this.bookingApi, payload);
  }

  fetchBookingStatus(bookingReferenceId: string): Observable<any> {
    this.initApiParams();
    const payload = {
      bookingReferenceId: bookingReferenceId,
    };
    return this.httpClient.postWithErorrHandler(
      this.fetchBookingStatusApi,
      payload
    );
  }

  getBookingDetails(bookingReferenceId: string): Observable<any> {
    this.initApiParams();
    const payload = {
      bookingReferenceId,
    };
    return this.httpClient.postWithErorrHandler(
      this.getBookingDetailsApi,
      payload
    );
  }

  getAirportServicesBookingHistory(
    serviceType: string = 'esim',
    page: number = 1,
    count: number = 10
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.serviceType = serviceType;
    this.apiParams.page = page;
    this.apiParams.count = count;
    return this.httpClient.getWithErorrHandler(this.getBookingHistoryApi);
  }

  getBookingPolicies(catlogType: string): Observable<any> {
    this.initApiParams();
    this.apiParams.catlogType = catlogType;
    return this.httpClient.getWithErorrHandler(this.getBookingPoliciesApi);
  }

  sendBookingEmail(
    bookingId: string,
    actionName: string,
    actionType: string
  ): Observable<any> {
    this.initApiParams();
    this.apiParams.action_name = actionName;
    this.apiParams.action_type = actionType;
    this.apiParams.action_id = bookingId;
    return this.httpClient.getWithErorrHandler(this.addUserActionApi);
  }

  getProductAvailability(payload: any): Observable<any> {
    this.initApiParams();
    return this.httpClient.postWithErorrHandler(
      this.productAvailabilityApi,
      payload
    );
  }

  private initApiParams(): void {
    this.apiParams = {};
  }
}
